import React, { useState, useRef, useCallback, useEffect } from 'react';
import './LiveTranscriptionRecorder.css';
import { useTranscriptionLimit } from '../hooks/useTranscriptionLimit';
import Icon from './Icons';

/**
 * LiveTranscriptionRecorder (Quick Transcription)
 *
 * Records audio and sends chunks to the server during recording.
 * Transcription is processed when recording stops, then displayed.
 */
const LiveTranscriptionRecorder = ({
  onTranscriptUpdate,
  onSessionComplete,
  onError,
  isAuthenticated = false,
  onLoginPrompt
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [liveSegments, setLiveSegments] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [latency, setLatency] = useState(null);
  const [volumeLevel, setVolumeLevel] = useState(0);

  const { checkLimit, LimitAlert } = useTranscriptionLimit();

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const sessionIdRef = useRef(null);
  const timerRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const recordingStartTimeRef = useRef(null);
  const chunkTimestampsRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const wsRef = useRef(null);
  const liveSegmentsRef = useRef([]); // Ref to track current segments for WebSocket closure

  // Timer for recording duration
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [isRecording, isPaused]);
   

  // Warn user when trying to leave page during recording
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRecording) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // Some browsers show this message
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isRecording]);

  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Monitor audio volume levels
  const monitorVolume = useCallback(() => {
    if (!analyserRef.current) {
      console.warn('[Volume] Analyser not available');
      return;
    }

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const updateVolume = () => {
      // Check if analyser is still available (could be cleared on stop)
      if (!analyserRef.current) {
        console.log('[Volume] Analyser removed, stopping volume monitoring');
        return;
      }

      try {
        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate RMS (root mean square) for better volume representation
        const sumSquares = dataArray.reduce((acc, val) => acc + (val * val), 0);
        const rms = Math.sqrt(sumSquares / dataArray.length);

        // Apply gain and scaling to make the indicator more responsive
        // Typical speaking voice RMS is around 20-40, so we scale and boost
        const gain = 3.0; // Boost the signal
        const volume = Math.min(100, (rms / 255) * 100 * gain);

        setVolumeLevel(volume);

        animationFrameRef.current = requestAnimationFrame(updateVolume);
      } catch (err) {
        console.error('[Volume] Error updating volume:', err);
      }
    };

    console.log('[Volume] Starting volume monitoring');
    updateVolume();
  }, []);

  // Cleanup volume monitoring
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Cleanup recording state (for error handling)
  const cleanupRecordingState = useCallback(() => {
    console.log('[WebSocket] Cleaning up recording state after error');

    // Stop recording
    setIsRecording(false);
    setIsPaused(false);

    // Clear segments
    setLiveSegments([]);
    liveSegmentsRef.current = [];

    // Stop media recorder if active
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {
        console.warn('[WebSocket] Error stopping media recorder:', e);
      }
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Stop volume monitoring
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(e => console.warn('[WebSocket] Error closing audio context:', e));
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setVolumeLevel(0);

    // Close WebSocket
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
    }

    // Reset session state
    sessionIdRef.current = null;
    chunkIndexRef.current = 0;
    recordingStartTimeRef.current = null;
    chunkTimestampsRef.current = [];
  }, []);

  // Start live transcription session
  const startLiveRecording = useCallback(async () => {
    if (!isAuthenticated) {
      if (onLoginPrompt) {
        onLoginPrompt();
      }
      return;
    }

    try {
      setError(null);
      setLiveSegments([]);
      liveSegmentsRef.current = []; // Reset ref as well
      setRecordingTime(0);
      chunkIndexRef.current = 0;
      chunkTimestampsRef.current = [];

      // Check transcription limits before starting recording
      const canProceed = await checkLimit();
      if (!canProceed) {
        return;
      }

      // Check for required APIs
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Your browser does not support audio recording. Please use a modern browser.');
        return;
      }

      if (!window.WebSocket) {
        setError('Your browser does not support WebSocket connections. Please use a modern browser.');
        return;
      }

      if (!window.MediaRecorder) {
        setError('Your browser does not support MediaRecorder. Please use Chrome, Firefox, or Safari.');
        return;
      }

      // Get auth token from localStorage
      const token = localStorage.getItem('authToken');

      // Verify auth token exists
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        if (onLoginPrompt) onLoginPrompt();
        return;
      }

      // Connect to WebSocket
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsUrl = `${protocol}://${window.location.host}/ws/live-transcribe?token=${encodeURIComponent(token)}`;

      console.log('[WebSocket] Connecting to:', wsUrl);
      wsRef.current = new WebSocket(wsUrl);

      // Add connection timeout
      const connectionTimeout = setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
          console.error('[WebSocket] Connection timeout');
          wsRef.current.close();
          setError('Connection timeout. Please check your internet connection and try again.');
          // Don't need to call cleanupRecordingState here as recording hasn't started yet
        }
      }, 10000); // 10 second timeout

      // Handle WebSocket connection open
      wsRef.current.onopen = () => {
        console.log('[WebSocket] Connected successfully');
        clearTimeout(connectionTimeout);

        // Read maxSilenceInterval from localStorage
        const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
        const maxSilenceInterval = preferences.maxSilenceInterval || 1.0;

        // Send start message
        wsRef.current.send(JSON.stringify({
          type: 'start',
          language: 'en',
          model: 'whisper-large-v3-turbo',
          maxSilenceInterval: maxSilenceInterval
        }));

        console.log('[WebSocket] Sent start message with maxSilenceInterval:', maxSilenceInterval);
      };

      // Handle incoming messages
      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('[WebSocket] Received message:', message.type);

          if (message.type === 'session_started') {
            sessionIdRef.current = message.sessionId;
            console.log('[WebSocket] Session started:', message.sessionId);

            // Now start recording
            startMediaRecorder();

          } else if (message.type === 'transcription') {
            // Update segments
            const newSegments = message.segments || [];
            console.log('[WebSocket] Received transcription with', newSegments.length, 'segments:', newSegments.map(s => ({ text: s.text, start: s.start })));

            setLiveSegments(prev => {
              console.log('[WebSocket] Current segments:', prev.length, prev.map(s => ({ text: s.text, start: s.start })));

              // Deduplicate segments based on text and timestamp
              const existingTexts = new Set(prev.map(s => `${s.text}_${s.start}`));
              const uniqueNewSegments = newSegments.filter(seg =>
                !existingTexts.has(`${seg.text}_${seg.start}`)
              );

              if (uniqueNewSegments.length === 0) {
                console.log('[WebSocket] All segments are duplicates, skipping');
                return prev;
              }

              console.log('[WebSocket] Added', uniqueNewSegments.length, 'unique segments:', uniqueNewSegments.map(s => ({ text: s.text, start: s.start })), 'total now:', prev.length + uniqueNewSegments.length);

              // Don't pass to parent during recording - only show in preview box
              // The final transcript will be loaded after session completes

              const merged = [...prev, ...uniqueNewSegments];
              // Keep ref in sync
              liveSegmentsRef.current = merged;
              return merged;
            });

            // Calculate latency
            const chunkInfo = chunkTimestampsRef.current.find(c => c.index === message.chunkIndex);
            if (chunkInfo) {
              const latencyMs = Date.now() - chunkInfo.sentAt;
              setLatency(latencyMs);
              console.log('[WebSocket] Latency:', latencyMs, 'ms');
            }

          } else if (message.type === 'session_complete') {
            // Handle completion - use segments from server if available, otherwise fall back to live segments
            const liveSegments = liveSegmentsRef.current;
            const serverSegments = message.transcript?.data?.segments || [];
            const currentSegments = serverSegments.length > 0 ? serverSegments : liveSegments;

            console.log('[WebSocket] Session complete:', {
              hasTranscript: !!message.transcript,
              transcriptId: message.transcript?.id,
              transcriptName: message.transcript?.name,
              liveSegmentsCount: liveSegments.length,
              serverSegmentsCount: serverSegments.length,
              usingSegments: serverSegments.length > 0 ? 'server' : 'live',
              fullMessage: message
            });

            setIsRecording(false);
            setIsPaused(false);

            // Clear live segments to hide preview box
            setLiveSegments([]);
            liveSegmentsRef.current = [];

            // Notify parent component with segments from server
            if (onSessionComplete) {
              if (message.transcript && message.transcript.id) {
                console.log('[WebSocket] Calling onSessionComplete with transcript:', message.transcript.id, 'and', currentSegments.length, 'segments');
                onSessionComplete(message.transcript, currentSegments);
              } else {
                console.error('[WebSocket] Session complete but no valid transcript:', message);
                // Still call onSessionComplete but with null transcript to trigger error handling
                onSessionComplete(null, currentSegments);
              }
            }

            // Reset state
            sessionIdRef.current = null;
            chunkIndexRef.current = 0;
            recordingStartTimeRef.current = null;
            chunkTimestampsRef.current = [];

          } else if (message.type === 'error') {
            console.error('[WebSocket] Server error:', message.message);
            setError(message.message || 'Server error occurred');
          }
        } catch (err) {
          console.error('[WebSocket] Error parsing message:', err);
        }
      };

      // Handle WebSocket errors
      wsRef.current.onerror = (err) => {
        console.error('[WebSocket] Connection error:', err);
        clearTimeout(connectionTimeout);
        setError('WebSocket connection failed. Please check your internet connection and try again.');

        // Clean up if we were recording
        if (isRecording) {
          cleanupRecordingState();
        }
      };

      // Handle WebSocket close
      wsRef.current.onclose = (event) => {
        console.log('[WebSocket] Connection closed:', event.code, event.reason);
        clearTimeout(connectionTimeout);

        if (event.code === 4401) {
          setError('Authentication failed. Please log in again.');
          if (onLoginPrompt) onLoginPrompt();
          if (isRecording) {
            cleanupRecordingState();
          }
        } else if (event.code === 1006) {
          setError('Connection closed abnormally. Please check your internet connection.');
          if (isRecording) {
            cleanupRecordingState();
          }
        } else if (isRecording && !event.wasClean) {
          setError('Connection lost during recording. Please try again.');
          cleanupRecordingState();
        }
      };

      // Wait for WebSocket to be ready before getting microphone
      // The actual recording will start in the onmessage handler when we get 'session_started'
      console.log('[WebSocket] Waiting for connection...');

      // Function to start the MediaRecorder (called when WebSocket session is ready)
      const startMediaRecorder = async () => {
        try {
          // Get microphone access
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 44100
            }
          });

          streamRef.current = stream;

          // Set up audio context for volume monitoring
          try {
            // Always create a fresh AudioContext for each recording session
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
              try {
                await audioContextRef.current.close();
              } catch (e) {
                console.warn('[Volume] Error closing previous AudioContext:', e);
              }
            }

            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            console.log('[Volume] Created new AudioContext, state:', audioContextRef.current.state);

            // Resume audio context if suspended (required in some browsers)
            // This happens due to browser autoplay policies
            if (audioContextRef.current.state === 'suspended') {
              console.log('[Volume] Resuming suspended AudioContext...');
              await audioContextRef.current.resume();
              console.log('[Volume] AudioContext resumed, new state:', audioContextRef.current.state);
            }

            const source = audioContextRef.current.createMediaStreamSource(stream);
            analyserRef.current = audioContextRef.current.createAnalyser();
            analyserRef.current.fftSize = 512;
            analyserRef.current.smoothingTimeConstant = 0.1; // Very responsive, matching AudioRecorder
            source.connect(analyserRef.current);

            console.log('[Volume] Audio pipeline connected, starting monitoring...');

            // Start monitoring immediately (no delay needed with fresh context)
            if (analyserRef.current) {
              monitorVolume();
              console.log('[Volume] Volume monitoring started, final AudioContext state:', audioContextRef.current.state);
            }
          } catch (err) {
            console.error('[Volume] Could not set up volume monitoring:', err);
            console.error('[Volume] Error details:', err.message, err.stack);
          }

          // Create MediaRecorder with best supported format
          let mimeType;
          const supportedTypes = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg;codecs=opus',
            'audio/mp4',
            'audio/mpeg'
          ];

          for (const type of supportedTypes) {
            if (MediaRecorder.isTypeSupported(type)) {
              mimeType = type;
              console.log('[WebSocket] Using mime type:', mimeType);
              break;
            }
          }

          if (!mimeType) {
            throw new Error('No supported audio format found for your browser');
          }

          const mediaRecorder = new MediaRecorder(stream, { mimeType });

          mediaRecorderRef.current = mediaRecorder;

          // Handle audio chunks
          mediaRecorder.ondataavailable = async (event) => {
            if (event.data.size > 0 && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              const offsetMs = Date.now() - recordingStartTimeRef.current;
              chunkTimestampsRef.current.push({ index: chunkIndexRef.current, sentAt: Date.now() });

              // Send chunk via WebSocket
              await sendAudioChunk(event.data, chunkIndexRef.current, offsetMs);
              chunkIndexRef.current++;
            }
          };

          // Start recording with 1-second chunks
          recordingStartTimeRef.current = Date.now();
          mediaRecorder.start(1000);

          setIsRecording(true);
          setIsPaused(false);

          console.log('[WebSocket] Recording started');
        } catch (err) {
          console.error('[WebSocket] Error starting media recorder:', err);

          let errorMessage = 'Failed to start recording';
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            errorMessage = 'Microphone access denied. Please allow microphone permissions.';
          } else if (err.name === 'NotFoundError') {
            errorMessage = 'No microphone found. Please connect a microphone.';
          }

          setError(errorMessage);
          if (onError) onError(err);

          // Close WebSocket
          if (wsRef.current) {
            wsRef.current.close();
          }
        }
      };

    } catch (err) {
      console.error('[LiveTranscription] Error starting recording:', err);

      let errorMessage = 'Failed to start live transcription';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Microphone access denied. Please allow microphone permissions.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone.';
      }

      setError(errorMessage);
      if (onError) onError(err);
    }
  }, [isAuthenticated, onLoginPrompt, onError, monitorVolume]);

  // Send audio chunk to server via WebSocket
  const sendAudioChunk = async (audioBlob, chunkIndex, offsetMs) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[WebSocket] Not connected, skipping chunk');
      return;
    }

    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];

        // Send via WebSocket
        wsRef.current.send(JSON.stringify({
          type: 'audio_chunk',
          data: base64,
          chunkIndex,
          offsetMs
        }));

        console.log('[WebSocket] Sent audio chunk:', chunkIndex, 'size:', audioBlob.size);
      };

      reader.onerror = (err) => {
        console.error('[WebSocket] Error reading audio blob:', err);
      };

      reader.readAsDataURL(audioBlob);

    } catch (err) {
      console.error('[WebSocket] Error sending chunk:', err);
    }
  };

  // Pause/Resume recording
  const togglePause = useCallback(async () => {
    if (!mediaRecorderRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    try {
      if (isPaused) {
        // Resume
        mediaRecorderRef.current.resume();

        wsRef.current.send(JSON.stringify({
          type: 'resume'
        }));

        setIsPaused(false);
        console.log('[WebSocket] Resumed');
      } else {
        // Pause
        mediaRecorderRef.current.pause();

        wsRef.current.send(JSON.stringify({
          type: 'pause'
        }));

        setIsPaused(true);
        console.log('[WebSocket] Paused');
      }
    } catch (err) {
      console.error('[WebSocket] Error toggling pause:', err);
    }
  }, [isPaused]);

  // Stop recording and finalize
  const stopLiveRecording = useCallback(async () => {
    if (!mediaRecorderRef.current) {
      console.warn('[WebSocket] Cannot stop - mediaRecorder not available');
      setIsRecording(false);
      setIsPaused(false);
      return;
    }

    console.log('[WebSocket] Stop button clicked, initiating stop sequence');

    try {
      // Stop volume monitoring
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        await audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      setVolumeLevel(0);

      console.log('[WebSocket] Stopping MediaRecorder');

      // Wait for MediaRecorder to fully stop (fires final ondataavailable then onstop)
      await new Promise((resolve) => {
        if (mediaRecorderRef.current) {
          mediaRecorderRef.current.onstop = () => {
            console.log('[MediaRecorder] Stopped, all chunks sent');
            resolve();
          };
          mediaRecorderRef.current.stop();
        } else {
          resolve();
        }
      });

      // Clean up stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      console.log('[WebSocket] Stopping session');

      // Send stop message via WebSocket AFTER all chunks have been sent
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'stop',
          transcriptName: `Live Transcript ${new Date().toLocaleString()}`
        }));

        console.log('[WebSocket] Sent stop message, waiting for session_complete...');

        // Set a timeout in case session_complete doesn't arrive
        setTimeout(() => {
          if (isRecording) {
            console.warn('[WebSocket] Timeout waiting for session_complete, forcing UI update');
            setIsRecording(false);
            setIsPaused(false);
            setError('Transcription may not have been saved properly. Please check your history.');
          }
        }, 30000); // 30 second timeout

        // The session_complete message will be received via WebSocket
        // and handled in the onmessage handler
      } else {
        console.warn('[WebSocket] Connection not available for stop message');
        setIsRecording(false);
        setIsPaused(false);
      }

    } catch (err) {
      console.error('[WebSocket] Error stopping recording:', err);
      setError('Failed to stop transcription');
      if (onError) onError(err);
    }
  }, [onError]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        console.log('[WebSocket] Cleaning up connection on unmount');
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  // Debug log when liveSegments changes
  useEffect(() => {
    console.log('[LiveTranscription] liveSegments state updated:', {
      length: liveSegments.length,
      segments: liveSegments
    });
  }, [liveSegments]);

  // Debug logging for render
  console.log('[LiveTranscription] Rendering component:', {
    isRecording,
    isPaused,
    liveSegmentsLength: liveSegments.length,
    liveSegmentsRefLength: liveSegmentsRef.current.length,
    volumeLevel,
    latency,
    error,
    hasSession: !!sessionIdRef.current
  });

  return (
    <div className="live-transcription-recorder">
      <LimitAlert />

      <div className="recorder-header">
        <h3>
          <span className={`live-indicator ${isRecording && !isPaused ? 'active' : ''}`}></span>
          Live Transcription
        </h3>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="recorder-controls">
        <div className="main-controls">
          {!isRecording && (
            <button
              className="control-btn record-btn live"
              onClick={startLiveRecording}
              title="Start Live Transcription"
            >
              <Icon name="microphone" size={24} />
            </button>
          )}

          {isRecording && (
            <>
              <button
                className="control-btn pause-btn"
                onClick={togglePause}
                title={isPaused ? 'Resume' : 'Pause'}
              >
                <Icon name={isPaused ? 'play' : 'pause'} size={24} />
              </button>

              <button
                className="control-btn stop-btn"
                onClick={stopLiveRecording}
                title="Stop and Save"
              >
                <Icon name="stop" size={24} />
              </button>
            </>
          )}
        </div>

        <div className="recording-info">
          <div className="timer">
            <span className="time">{formatTime(recordingTime)}</span>
          </div>
 
          {isRecording && (
            <>
              <div className="recording-status">
                {isPaused ? 'Paused' : isProcessing ? 'Processing...' : 'Recording...'}
              </div>
              {latency && (
                <div className="latency-indicator">
                  <span className="latency-label">Latency:</span>
                  <span className={`latency-value ${latency < 500 ? 'good' : latency < 1000 ? 'moderate' : 'poor'}`}>
                    {latency}ms
                  </span>
                </div>
              )}
              <div className="volume-indicator">
                <Icon name="microphone" size={20} className="volume-meter-label" />
                <div className="volume-meter-bar">
                  <div
                    className="volume-meter-fill"
                    style={{ width: `${volumeLevel}%` }}
                  ></div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {isRecording && (
	<div className="live-transcript-preview">
	      <h4>Transcription</h4>
	      <div className="transcript-segments">
	      {liveSegments.length > 0 ? (
		      liveSegments.map((segment, index) => {
			      const segmentStart = segment.start || 0;
			      const segmentEnd = segment.end || segmentStart + 2;
			      return (
				 <div key={`${segment.start}-${index}`} className="segment">
				    <div className="segment-time">
				        {formatTime(segmentStart)} - {formatTime(segmentEnd)}
				    </div>
				    {segment.speaker && (
					    <div className="segment-speaker">
					    	Speaker {segment.speaker}
					    </div>
				    )}
				   <div className="segment-text">
				      {segment.text}
				   </div>
				 </div>
			      );
		      })
	      ) : (
		      <div className="segment">
		          <div className="segment-text" style={{ fontStyle: 'italic', color: 'var(--color-text-secondary)' }}>
		            Listening... speak to see transcription appear here
		          </div>
		      </div>
	      )}
	      </div>
	  </div>
      )}
    </div>
  );
};

export default LiveTranscriptionRecorder;
